import { z } from 'zod';
import { cashFlowEntrySchema, type CashFlowEntry } from '@/lib/cashFlow';
import {
  buildTaxScheduleRows,
  LOAN_SCHEDULE_TYPES,
  loanInstallments,
  loanScheduleTotals,
  toCents,
  validateImportedSchedule,
  MAX_TERM_MONTHS,
  type ImportedScheduleRow,
  type LoanInstallment,
  type LoanScheduleSource,
  type LoanScheduleTotals,
  type LoanScheduleType,
  type LoanTerms,
  type TaxScheduleItem,
} from '@progear/finance-core';

export type { ImportedScheduleRow, TaxScheduleItem } from '@progear/finance-core';

/**
 * Laenud & palgafond client (replit.md Module 1.7 UI). Talks to `/api/loans` +
 * `/api/payroll` and Zod-validates every payload at the boundary (anti-slop —
 * external data is never trusted). The API already answers in camelCase, so the
 * wire schema IS the domain shape (no snake_case mapping layer like sync.ts).
 *
 * Money maths policy (L9 + master rule): every derivation here is INTEGER-cents
 * arithmetic on top of finance-core's `loanSchedule` — no component does its own
 * VAT/sum/margin/interest calculation; components only render what these helpers
 * return.
 */

export const LOAN_KINDS = ['bank', 'owner', 'tax_debt'] as const;
export type LoanKind = (typeof LOAN_KINDS)[number];

export const OWNER_INTEREST_MODES = ['accrued', 'paid_monthly'] as const;
export type OwnerInterestMode = (typeof OWNER_INTEREST_MODES)[number];

export const LOAN_SCHEDULE_SOURCES = ['computed', 'contract'] as const;

/** Display-only: whether the contract quotes the rate per year or per month
 *  (`annualRateBps` stays ANNUAL either way — migration 0046 convention). */
export const RATE_PERIODS = ['annual', 'monthly'] as const;
export type RatePeriod = (typeof RATE_PERIODS)[number];

export const RATE_PERIOD_LABELS_ET: Record<RatePeriod, string> = {
  annual: '% aastas',
  monthly: '% kuus',
};

/** Estonian dropdown labels for the schedule types (UI stays Estonian). */
export const SCHEDULE_TYPE_LABELS_ET: Record<LoanScheduleType, string> = {
  annuity: 'Annuiteet',
  equal_principal: 'Võrdne põhiosa',
  bullet: 'Bullet (põhiosa lõpus)',
  balloon: 'Jääkväärtusega (balloon)',
};

/* ------------------------------------------------------------------ wire */

const ownerTermsSchema = z.object({
  shareholder: z.string(),
  interestMode: z.enum(OWNER_INTEREST_MODES),
  note: z.string().nullable(),
});

const loanSchema = z.object({
  id: z.string(),
  name: z.string(),
  lender: z.string(),
  loanKind: z.enum(LOAN_KINDS),
  scheduleType: z.enum(LOAN_SCHEDULE_TYPES),
  scheduleSource: z.enum(LOAN_SCHEDULE_SOURCES),
  principalCents: z.number().int(),
  annualRateBps: z.number().int(),
  ratePeriod: z.enum(RATE_PERIODS),
  termMonths: z.number().int(),
  firstPaymentDate: z.string(),
  residualValueCents: z.number().int().nullable(),
  drawdownDate: z.string().nullable(),
  drawdownCents: z.number().int().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  ownerTerms: ownerTermsSchema.nullable(),
});

/** One imported annex row (1:1 finance-core `ImportedScheduleRow`). */
const importedScheduleRowSchema = z.object({
  seq: z.number().int(),
  dueDate: z.string(),
  principalCents: z.number().int(),
  interestCents: z.number().int(),
  feeCents: z.number().int(),
  totalCents: z.number().int(),
  balanceBeforeCents: z.number().int(),
});

/** A dated one-off contract fee (lepingutasu) — ONE nullable pair, like the DB CHECK. */
const contractFeeSchema = z.object({ date: z.string(), cents: z.number().int() });

/** The stored contract block of one loan (`GET /loans/:id/contract`). The PUT
 *  body is a FULL REPLACE, so the editor loads this and resends it — otherwise
 *  the annex would be wiped on an ordinary edit. */
const loanContractSchema = z.object({
  contractNo: z.string(),
  fee: contractFeeSchema.nullable(),
  collateral: z.string().nullable(),
  sureties: z.string().nullable(),
  lenderDetails: z.string().nullable(),
  note: z.string().nullable(),
  scheduleRows: z.array(importedScheduleRowSchema),
});

const materializationSchema = z.object({
  upserted: z.number().int(),
  deletedStale: z.number().int(),
  protectedCount: z.number().int(),
});

/** Line-settlement status codes (per employee AND per month) — the ONE tuple both
 *  the settle pane and the month grid badge read. */
export const PAYROLL_LINE_STATUSES = ['unpaid', 'partial', 'settled'] as const;
export type PayrollLineStatusCode = (typeof PAYROLL_LINE_STATUSES)[number];

/** Base payroll month (the two entered figures) — what PUT/DELETE echo back. */
const payrollMonthSchema = z.object({
  month: z.string(),
  netFundCents: z.number().int(),
  tsdCents: z.number().int(),
  note: z.string().nullable(),
  updatedAt: z.string(),
});

/** Month-level settlement of the net-wage entry, derived server-side via
 *  finance-core `payrollMonthSettlementOf` (L9 + master rule) — the grid badge
 *  renders it, never its own math. */
const payrollMonthSettlementSchema = z.object({
  paidCents: z.number().int(),
  outstandingCents: z.number().int(),
  status: z.enum(PAYROLL_LINE_STATUSES),
});

/** A payroll month AS LISTED (`GET /payroll`): the base figures PLUS the net-wage
 *  entry's settlement (null when no net entry is materialized yet). */
const payrollListMonthSchema = payrollMonthSchema.extend({
  settlement: payrollMonthSettlementSchema.nullable(),
});

/** PUT /payroll/:month acknowledges an employee-line sync when the body carried one. */
const employeeSyncSchema = z.object({
  imported: z.number().int(),
  removed: z.number().int(),
  warnings: z.array(z.string()),
});

/** The operator prefill assembled server-side from one contract PDF (AI locates,
 *  code computes — L6): every number already code-derived + chain-checked. */
const contractPrefillSchema = z.object({
  contractNo: z.string().nullable(),
  lender: z.string().nullable(),
  ratePeriod: z.enum(RATE_PERIODS),
  annualRateBps: z.number().int().nullable(),
  principalCents: z.number().int().nullable(),
  termMonths: z.number().int(),
  firstPaymentDate: z.string().nullable(),
  fee: contractFeeSchema.nullable(),
  collateral: z.string().nullable(),
  sureties: z.string().nullable(),
  lenderDetails: z.string().nullable(),
  scheduleRows: z.array(importedScheduleRowSchema),
  scheduleValid: z.boolean(),
  scheduleError: z.string().nullable(),
  warnings: z.array(z.string()),
});

const contractParseResponseSchema = z.object({
  parsed: contractPrefillSchema,
  // The AI cost fact (L6: every call logged in €) — voiced to the operator.
  ai: z.object({ costCents: z.number().int(), model: z.string() }),
});

const loanContractResponseSchema = z.object({ contract: loanContractSchema.nullable() });

const loansResponseSchema = z.object({ loans: z.array(loanSchema) });
const loanWriteResponseSchema = z.object({ loan: loanSchema, materialization: materializationSchema });
const deleteResponseSchema = z.object({ deleted: z.literal(true), materialization: materializationSchema });
const payrollResponseSchema = z.object({ months: z.array(payrollListMonthSchema) });
const payrollWriteResponseSchema = z.object({
  month: payrollMonthSchema,
  materialization: materializationSchema,
  // Present only when the write carried an employee list (palgalehe import).
  employees: employeeSyncSchema.optional(),
});

export type Loan = z.infer<typeof loanSchema>;
export type LoanOwnerTerms = z.infer<typeof ownerTermsSchema>;
export type LoanContract = z.infer<typeof loanContractSchema>;
export type ContractFee = z.infer<typeof contractFeeSchema>;
export type ContractImportPrefill = z.infer<typeof contractPrefillSchema>;
export type ContractParseResult = z.infer<typeof contractParseResponseSchema>;
export type Materialization = z.infer<typeof materializationSchema>;
export type PayrollMonthBase = z.infer<typeof payrollMonthSchema>;
export type PayrollMonthSettlement = z.infer<typeof payrollMonthSettlementSchema>;
export type PayrollMonth = z.infer<typeof payrollListMonthSchema>;
export type EmployeeSync = z.infer<typeof employeeSyncSchema>;

export interface LoanWriteResult {
  loan: Loan;
  materialization: Materialization;
}

export interface PayrollWriteResult {
  month: PayrollMonthBase;
  materialization: Materialization;
  employees?: EmployeeSync;
}

/* ------------------------------------------------------------------ fetchers */

export interface LoanRequestOptions {
  /** Injectable fetch (tests). Defaults to the global fetch. */
  fetchImpl?: typeof fetch;
  /** Base URL; defaults to same-origin (relative) so it works everywhere. */
  baseUrl?: string;
  /** Supabase access token; attached as a Bearer header when present. */
  accessToken?: string;
}

function authHeaders(accessToken: string | undefined, base: Record<string, string> = {}): Record<string, string> {
  return accessToken ? { ...base, Authorization: `Bearer ${accessToken}` } : base;
}

const errorEnvelopeSchema = z.object({ error: z.object({ message: z.string() }) });

/**
 * Pull the API error envelope's message out of a failed response so the operator
 * sees WHY a save failed (validation, conflict …) — falling back to the bare
 * status when the body carries no envelope.
 */
async function failureMessage(res: Response, fallback: string): Promise<string> {
  const body: unknown = await res.json().catch(() => null);
  const parsed = errorEnvelopeSchema.safeParse(body);
  return parsed.success ? parsed.data.error.message : fallback;
}

/** The full loan roster (portfolio table + editor prefill). */
export async function fetchLoans(options: LoanRequestOptions = {}): Promise<Loan[]> {
  const { fetchImpl = fetch, baseUrl = '', accessToken } = options;
  const res = await fetchImpl(`${baseUrl}/api/loans`, { headers: authHeaders(accessToken) });
  if (!res.ok) {
    throw new Error(`Loans request failed: ${res.status}`);
  }
  const parsed = loansResponseSchema.parse(await res.json());
  return parsed.loans;
}

/** The contract block of a write body — presence IS the schedule-source switch
 *  (contract → 'contract', absent → 'computed'; the API convention). */
export interface LoanContractDraft {
  contractNo: string;
  fee: ContractFee | null;
  collateral: string | null;
  sureties: string | null;
  lenderDetails: string | null;
  note: string | null;
  scheduleRows: ImportedScheduleRow[];
}

/** The write body for POST/PUT — assembled by {@link buildLoanDraft}. */
export interface LoanDraft {
  loanKind: LoanKind;
  name: string;
  lender: string;
  scheduleType: LoanScheduleType;
  principalCents: number;
  annualRateBps: number;
  ratePeriod: RatePeriod;
  termMonths: number;
  firstPaymentDate: string;
  residualValueCents?: number;
  drawdown: { date: string; cents: number } | null;
  contract?: LoanContractDraft;
  ownerTerms?: { shareholder: string; interestMode: OwnerInterestMode; note: string | null };
}

/** Create (id null) or replace (id set) one loan; the API re-materializes the calendar. */
export async function saveLoan(
  draft: LoanDraft,
  id: string | null,
  options: LoanRequestOptions = {},
): Promise<LoanWriteResult> {
  const { fetchImpl = fetch, baseUrl = '', accessToken } = options;
  const url = id ? `${baseUrl}/api/loans/${encodeURIComponent(id)}` : `${baseUrl}/api/loans`;
  const res = await fetchImpl(url, {
    method: id ? 'PUT' : 'POST',
    headers: authHeaders(accessToken, { 'Content-Type': 'application/json' }),
    body: JSON.stringify(draft),
  });
  if (!res.ok) {
    throw new Error(await failureMessage(res, `Loan save failed: ${res.status}`));
  }
  return loanWriteResponseSchema.parse(await res.json());
}

/** One loan's stored contract block — null for a computed (hand-entered) loan. */
export async function fetchLoanContract(
  loanId: string,
  options: LoanRequestOptions = {},
): Promise<LoanContract | null> {
  const { fetchImpl = fetch, baseUrl = '', accessToken } = options;
  const res = await fetchImpl(`${baseUrl}/api/loans/${encodeURIComponent(loanId)}/contract`, {
    headers: authHeaders(accessToken),
  });
  if (!res.ok) {
    throw new Error(`Loan contract request failed: ${res.status}`);
  }
  return loanContractResponseSchema.parse(await res.json()).contract;
}

/** One chosen contract PDF: its file name + already-read bare base64. */
export interface ContractPdfFile {
  name: string;
  pdfBase64: string;
}

/** AI-assisted contract parse (L6: AI locates, code computes; writes NOTHING —
 *  the reviewed save goes through the ordinary {@link saveLoan}). */
export async function parseLoanContractPdf(
  file: ContractPdfFile,
  options: LoanRequestOptions = {},
): Promise<ContractParseResult> {
  const { fetchImpl = fetch, baseUrl = '', accessToken } = options;
  const res = await fetchImpl(`${baseUrl}/api/loans/import/parse`, {
    method: 'POST',
    headers: authHeaders(accessToken, { 'Content-Type': 'application/json' }),
    body: JSON.stringify(file),
  });
  if (!res.ok) {
    throw new Error(await failureMessage(res, `Lepingu lugemine ebaõnnestus: ${res.status}`));
  }
  return contractParseResponseSchema.parse(await res.json());
}

/** Delete one loan; the API removes its pending future calendar rows. */
export async function deleteLoan(id: string, options: LoanRequestOptions = {}): Promise<Materialization> {
  const { fetchImpl = fetch, baseUrl = '', accessToken } = options;
  const res = await fetchImpl(`${baseUrl}/api/loans/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers: authHeaders(accessToken),
  });
  if (!res.ok) {
    throw new Error(await failureMessage(res, `Loan delete failed: ${res.status}`));
  }
  return deleteResponseSchema.parse(await res.json()).materialization;
}

/* ------------------------------------------------- loan installments (maksegraafik) */

export const LOAN_INSTALLMENT_PARTS = ['principal', 'interest', 'fee'] as const;
export type LoanInstallmentPart = (typeof LOAN_INSTALLMENT_PARTS)[number];

/** One materialized schedule row's settlement state — every number derived
 *  SERVER-side via finance-core `outstandingOf` (L9 + master rule); the editor's
 *  Staatus column only renders. */
const loanInstallmentRowSchema = z.object({
  entryId: z.string(),
  seq: z.number().int(),
  part: z.enum(LOAN_INSTALLMENT_PARTS),
  expectedDate: z.string(),
  grossCents: z.number().int(),
  outstandingCents: z.number().int(),
  settled: z.boolean(),
});

const installmentsResponseSchema = z.object({
  installments: z.array(loanInstallmentRowSchema),
});
const markInstallmentsResponseSchema = z.object({
  recordedCount: z.number().int(),
  recordedCents: z.number().int(),
  entries: z.array(cashFlowEntrySchema),
});

export type LoanInstallmentStatus = z.infer<typeof loanInstallmentRowSchema>;

export interface MarkInstallmentsResult {
  recordedCount: number;
  recordedCents: number;
  entries: CashFlowEntry[];
}

/** The materialized schedule's live settlement state (editor Staatus column). */
export async function fetchLoanInstallments(
  loanId: string,
  options: LoanRequestOptions = {},
): Promise<LoanInstallmentStatus[]> {
  const { fetchImpl = fetch, baseUrl = '', accessToken } = options;
  const res = await fetchImpl(`${baseUrl}/api/loans/${encodeURIComponent(loanId)}/installments`, {
    headers: authHeaders(accessToken),
  });
  if (!res.ok) {
    throw new Error(`Loan installments request failed: ${res.status}`);
  }
  return installmentsResponseSchema.parse(await res.json()).installments;
}

export interface MarkInstallmentsDraft {
  /** Installment numbers (schedule seq) — every still-open row of each is settled. */
  seqs: number[];
  /** ISO day override; omitted = each row books on its OWN expected date. */
  paidOn?: string;
  /** Client idempotency key — a retry collapses onto the SAME payment events. */
  idempotencyKey: string;
}

/** Bulk mark-paid of selected installments (amounts recomputed server-side). */
export async function markLoanInstallmentsPaid(
  loanId: string,
  draft: MarkInstallmentsDraft,
  options: LoanRequestOptions = {},
): Promise<MarkInstallmentsResult> {
  const { fetchImpl = fetch, baseUrl = '', accessToken } = options;
  const res = await fetchImpl(
    `${baseUrl}/api/loans/${encodeURIComponent(loanId)}/installments/mark-paid`,
    {
      method: 'POST',
      headers: authHeaders(accessToken, { 'Content-Type': 'application/json' }),
      body: JSON.stringify({
        seqs: draft.seqs,
        idempotencyKey: draft.idempotencyKey,
        ...(draft.paidOn ? { paidOn: draft.paidOn } : {}),
      }),
    },
  );
  if (!res.ok) {
    throw new Error(await failureMessage(res, `Tasutuks märkimine ebaõnnestus: ${res.status}`));
  }
  return markInstallmentsResponseSchema.parse(await res.json());
}

/** One schedule installment's aggregated settlement (principal + interest rows
 *  summed per seq) — the editor's Staatus column and bulk selection read THIS.
 *  Status codes reuse the shared settlement tuple (same Estonian labels). */
export interface InstallmentSeqStatus {
  seq: number;
  grossCents: number;
  outstandingCents: number;
  status: PayrollLineStatusCode;
  /** Something is still open — the row gets a bulk-selection checkbox. */
  selectable: boolean;
}

/**
 * Aggregate the materialized rows per installment seq (integer-cents sums HERE,
 * never in the component — L9). Tolerant by design: seqs absent from the map
 * (schedule edited but not yet saved, drawdown-only loans …) simply render no
 * status.
 */
export function summarizeInstallments(
  rows: LoanInstallmentStatus[],
): Map<number, InstallmentSeqStatus> {
  const out = new Map<number, InstallmentSeqStatus>();
  for (const row of rows) {
    const prev = out.get(row.seq);
    const grossCents = (prev?.grossCents ?? 0) + row.grossCents;
    const outstandingCents = (prev?.outstandingCents ?? 0) + row.outstandingCents;
    out.set(row.seq, {
      seq: row.seq,
      grossCents,
      outstandingCents,
      status:
        outstandingCents === 0 ? 'settled' : outstandingCents < grossCents ? 'partial' : 'unpaid',
      selectable: outstandingCents > 0,
    });
  }
  return out;
}

/** Every saved payroll month (the view slices these per year). */
export async function fetchPayrollMonths(options: LoanRequestOptions = {}): Promise<PayrollMonth[]> {
  const { fetchImpl = fetch, baseUrl = '', accessToken } = options;
  const res = await fetchImpl(`${baseUrl}/api/payroll`, { headers: authHeaders(accessToken) });
  if (!res.ok) {
    throw new Error(`Payroll request failed: ${res.status}`);
  }
  const parsed = payrollResponseSchema.parse(await res.json());
  return parsed.months;
}

export interface PayrollDraft {
  netFundCents: number;
  tsdCents: number;
  note: string | null;
  /** Palgalehe import: REPLACES the month's employee-line set by name (the settle
   *  pane's targets). Omitted = terms-only save, existing lines untouched. */
  employees?: { name: string; grossCents: number; netCents: number }[];
}

/** Upsert one payroll month (`YYYY-MM`); the API re-materializes its two calendar rows. */
export async function savePayrollMonth(
  month: string,
  draft: PayrollDraft,
  options: LoanRequestOptions = {},
): Promise<PayrollWriteResult> {
  const { fetchImpl = fetch, baseUrl = '', accessToken } = options;
  const res = await fetchImpl(`${baseUrl}/api/payroll/${encodeURIComponent(month)}`, {
    method: 'PUT',
    headers: authHeaders(accessToken, { 'Content-Type': 'application/json' }),
    body: JSON.stringify(draft),
  });
  if (!res.ok) {
    throw new Error(await failureMessage(res, `Payroll save failed: ${res.status}`));
  }
  return payrollWriteResponseSchema.parse(await res.json());
}

/** Delete one payroll month; the API removes its pending future calendar rows. */
export async function deletePayrollMonth(
  month: string,
  options: LoanRequestOptions = {},
): Promise<Materialization> {
  const { fetchImpl = fetch, baseUrl = '', accessToken } = options;
  const res = await fetchImpl(`${baseUrl}/api/payroll/${encodeURIComponent(month)}`, {
    method: 'DELETE',
    headers: authHeaders(accessToken),
  });
  if (!res.ok) {
    throw new Error(await failureMessage(res, `Payroll delete failed: ${res.status}`));
  }
  return deleteResponseSchema.parse(await res.json()).materialization;
}

/* ------------------------------------------------------- palgaleht import */

const parsedPayrollEmployeeSchema = z.object({
  name: z.string(),
  grossCents: z.number().int(),
  netCents: z.number().int(),
});

/** The deterministic parser's preview (NO write happened; nulls = not found).
 *  When several PDFs are imported for one month it is the merged preview. */
const parsedPalgalehtSchema = z.object({
  month: z.string().nullable(),
  employees: z.array(parsedPayrollEmployeeSchema),
  wageFundCents: z.number().int().nullable(),
  taxesCents: z.number().int().nullable(),
  netFundCents: z.number().int().nullable(),
  checksumOk: z.boolean().nullable(),
  /** Non-fatal notes (skipped duplicate file, dropped rows, missing totals, …). */
  warnings: z.array(z.string()),
});

const parseResponseSchema = z.object({ parsed: parsedPalgalehtSchema });

export type ParsedPayrollEmployee = z.infer<typeof parsedPayrollEmployeeSchema>;
export type ParsedPalgaleht = z.infer<typeof parsedPalgalehtSchema>;

/** One PDF to parse — base64 (no data-URL prefix) plus its file name (shown in
 *  merge warnings/errors only, never persisted). */
export interface PayrollPdfFile {
  name: string;
  pdfBase64: string;
}

/** Parse one or more SmartAccounts palgaleht PDFs of ONE month into a single
 *  merged preview — deterministic, NO AI, NO write. A 422 carries the Estonian
 *  reason (unreadable file, differing months, conflicting employee amounts). */
export async function parsePayrollPdf(
  files: PayrollPdfFile[],
  options: LoanRequestOptions = {},
): Promise<ParsedPalgaleht> {
  const { fetchImpl = fetch, baseUrl = '', accessToken } = options;
  const res = await fetchImpl(`${baseUrl}/api/payroll/import/parse`, {
    method: 'POST',
    headers: authHeaders(accessToken, { 'Content-Type': 'application/json' }),
    body: JSON.stringify({ files }),
  });
  if (!res.ok) {
    throw new Error(await failureMessage(res, `Palgalehe lugemine ebaõnnestus: ${res.status}`));
  }
  return parseResponseSchema.parse(await res.json()).parsed;
}

/* ------------------------------------------- per-employee settlement */

/** Estonian badge label per line status — the ONE place each status is named. */
export const PAYROLL_LINE_STATUS_LABELS_ET: Record<PayrollLineStatusCode, string> = {
  unpaid: 'Maksmata',
  partial: 'Osaliselt',
  settled: 'Tasutud',
};

const employeePaymentSchema = z.object({
  id: z.string(),
  amountCents: z.number().int(),
  paidOn: z.string(),
});

/** One employee's settle-pane row — every number derived SERVER-side via
 *  finance-core (payrollLineOutstandingOf / payrollLineStatusOf / cap); the
 *  pane only renders (L9 + master rule). */
const employeeLineSchema = z.object({
  id: z.string(),
  name: z.string(),
  grossCents: z.number().int(),
  netCents: z.number().int(),
  paidCents: z.number().int(),
  outstandingCents: z.number().int(),
  status: z.enum(PAYROLL_LINE_STATUSES),
  capCents: z.number().int(),
  payments: z.array(employeePaymentSchema),
});

const employeeTotalsSchema = z.object({
  netCents: z.number().int(),
  paidCents: z.number().int(),
  outstandingCents: z.number().int(),
  settledLines: z.number().int(),
  lineCount: z.number().int(),
});

const employeesResponseSchema = z.object({
  /** The month's `payroll:<M>:net` calendar entry — null when not materialized. */
  entry: z.object({ id: z.string(), outstandingCents: z.number().int() }).nullable(),
  lines: z.array(employeeLineSchema),
  totals: employeeTotalsSchema,
});

const paymentWriteResponseSchema = z.object({ entry: cashFlowEntrySchema });
const paymentDeleteResponseSchema = z.object({
  deleted: z.literal(true),
  entry: cashFlowEntrySchema.nullable(),
});
const settleRemainingResponseSchema = z.object({
  entry: cashFlowEntrySchema,
  recordedCount: z.number().int(),
  recordedCents: z.number().int(),
});

export type PayrollEmployeePayment = z.infer<typeof employeePaymentSchema>;
export type PayrollEmployeeLine = z.infer<typeof employeeLineSchema>;
export type PayrollEmployeeTotals = z.infer<typeof employeeTotalsSchema>;
export type PayrollEmployeesResult = z.infer<typeof employeesResponseSchema>;

export interface SettleRemainingResult {
  entry: CashFlowEntry;
  recordedCount: number;
  recordedCents: number;
}

/** The settle pane's read surface: lines + payments + the entry's open remainder. */
export async function fetchPayrollEmployees(
  month: string,
  options: LoanRequestOptions = {},
): Promise<PayrollEmployeesResult> {
  const { fetchImpl = fetch, baseUrl = '', accessToken } = options;
  const res = await fetchImpl(`${baseUrl}/api/payroll/${encodeURIComponent(month)}/employees`, {
    headers: authHeaders(accessToken),
  });
  if (!res.ok) {
    throw new Error(`Payroll employees request failed: ${res.status}`);
  }
  return employeesResponseSchema.parse(await res.json());
}

export interface EmployeePaymentDraft {
  amountCents: number;
  /** ISO day; omitted = the API's Tallinn today. */
  paidOn?: string;
  /** Client idempotency key — a retry collapses onto ONE payment (audit-safe). */
  idempotencyKey: string;
}

/** Mark (part of) one employee's net wage paid; mirrors onto the month entry. */
export async function recordPayrollEmployeePayment(
  month: string,
  lineId: string,
  draft: EmployeePaymentDraft,
  options: LoanRequestOptions = {},
): Promise<CashFlowEntry> {
  const { fetchImpl = fetch, baseUrl = '', accessToken } = options;
  const res = await fetchImpl(
    `${baseUrl}/api/payroll/${encodeURIComponent(month)}/employees/${encodeURIComponent(lineId)}/payments`,
    {
      method: 'POST',
      headers: authHeaders(accessToken, { 'Content-Type': 'application/json' }),
      body: JSON.stringify(draft),
    },
  );
  if (!res.ok) {
    throw new Error(await failureMessage(res, `Makse salvestamine ebaõnnestus: ${res.status}`));
  }
  return paymentWriteResponseSchema.parse(await res.json()).entry;
}

/** Undo one employee payment (with its mirrored entry event; audit-logged). */
export async function deletePayrollEmployeePayment(
  paymentId: string,
  options: LoanRequestOptions = {},
): Promise<CashFlowEntry | null> {
  const { fetchImpl = fetch, baseUrl = '', accessToken } = options;
  const res = await fetchImpl(`${baseUrl}/api/payroll/employee-payments/${encodeURIComponent(paymentId)}`, {
    method: 'DELETE',
    headers: authHeaders(accessToken),
  });
  if (!res.ok) {
    throw new Error(await failureMessage(res, `Makse eemaldamine ebaõnnestus: ${res.status}`));
  }
  return paymentDeleteResponseSchema.parse(await res.json()).entry;
}

/** Settle EVERY open line's remainder at once (amounts recomputed server-side). */
export async function settlePayrollRemaining(
  month: string,
  idempotencyKey: string,
  paidOn: string | undefined,
  options: LoanRequestOptions = {},
): Promise<SettleRemainingResult> {
  const { fetchImpl = fetch, baseUrl = '', accessToken } = options;
  const res = await fetchImpl(`${baseUrl}/api/payroll/${encodeURIComponent(month)}/settle-remaining`, {
    method: 'POST',
    headers: authHeaders(accessToken, { 'Content-Type': 'application/json' }),
    body: JSON.stringify({ idempotencyKey, ...(paidOn ? { paidOn } : {}) }),
  });
  if (!res.ok) {
    throw new Error(await failureMessage(res, `Tasutuks märkimine ebaõnnestus: ${res.status}`));
  }
  return settleRemainingResponseSchema.parse(await res.json());
}

const PAYROLL_NET_EXTERNAL_ID = /^payroll:(\d{4}-\d{2}):net$/;

/** The `YYYY-MM` of a payroll net-wage entry, or null for every other entry.
 *  The ONE shared detector (L14) — the detail pane swaps in the per-employee
 *  settle surface and the register hides whole-entry mark-paid through it. */
export function payrollNetEntryMonth(externalId: string | null): string | null {
  const match = externalId === null ? null : PAYROLL_NET_EXTERNAL_ID.exec(externalId);
  return match ? match[1] : null;
}

/* ------------------------------------------------------------------ input parsing */

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** True when `iso` is a real calendar day (finance-core rejects e.g. Feb 30). */
export function isValidIsoDate(iso: string): boolean {
  if (!ISO_DATE.test(iso)) {
    return false;
  }
  const [y, m, d] = iso.split('-').map(Number);
  const days = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return m >= 1 && m <= 12 && d >= 1 && d <= days;
}

/** Strip Estonian formatting (spaces, nbsp, €), normalise the decimal comma. */
function normaliseNumeric(text: string, unit: string): string {
  return text.replace(/[\s\u00a0\u202f]/g, '').replace(unit, '').replace(',', '.');
}

/**
 * Parse an Estonian money input ("250 000,00") into POSITIVE integer cents.
 * Returns null on anything unparseable/non-positive — the caller renders the
 * invalid state; nothing is silently coerced. `toCents` absorbs binary drift (L9).
 */
export function parseEurInput(text: string): number | null {
  const cleaned = normaliseNumeric(text, '€');
  if (!/^\d+(\.\d{1,2})?$/.test(cleaned)) {
    return null;
  }
  const cents = toCents(Number(cleaned));
  return cents > 0 ? cents : null;
}

/** Parse a percent input ("4,9") into integer basis points (0 … 10000). */
export function parseRateInput(text: string): number | null {
  const cleaned = normaliseNumeric(text, '%');
  if (!/^\d+(\.\d{1,2})?$/.test(cleaned)) {
    return null;
  }
  const bps = Math.round(Number(cleaned) * 100);
  return bps <= 10000 ? bps : null;
}

/**
 * Rate input + its quoted period → ANNUAL basis points (the storage
 * convention). A monthly quote is ×12 in integer bps space; the same
 * ≤ 10000 bps (100 %/a) cap applies to the RESULT, so both periods obey
 * one rule.
 */
export function rateInputToAnnualBps(text: string, period: RatePeriod): number | null {
  const bps = parseRateInput(text);
  if (bps === null) {
    return null;
  }
  if (period === 'annual') {
    return bps;
  }
  const annual = bps * 12;
  return annual <= 10000 ? annual : null;
}

/** Parse a whole-month term ("60") into 1 … MAX_TERM_MONTHS. */
export function parseTermInput(text: string): number | null {
  const cleaned = text.replace(/[\s\u00a0\u202f]/g, '');
  if (!/^\d+$/.test(cleaned)) {
    return null;
  }
  const months = Number(cleaned);
  return months >= 1 && months <= MAX_TERM_MONTHS ? months : null;
}

/* ------------------------------------------------------------------ editor form */

/** Raw editor field state (all text) — assembled into a LoanDraft on the fly. */
export interface LoanFormFields {
  name: string;
  lender: string;
  scheduleType: LoanScheduleType;
  principalText: string;
  rateText: string;
  /** Which period the rate INPUT is quoted in — the draft always stores ANNUAL bps. */
  ratePeriod: RatePeriod;
  termText: string;
  firstPaymentDate: string;
  residualText: string;
  hasDrawdown: boolean;
  drawdownDate: string;
  drawdownText: string;
  /** The loan's kind — the editor flips bank↔owner; `tax_debt` is set by the
   *  tax-debt entry dialog and NEVER changed by the editor (an edit of an
   *  existing tax debt must keep its kind, or a PUT would silently flip it). */
  loanKind: LoanKind;
  shareholder: string;
  interestMode: OwnerInterestMode;
  ownerNote: string;
  /** Contract facts (used only when the form carries imported schedule rows). */
  contractNo: string;
  feeDate: string;
  feeText: string;
  collateral: string;
  sureties: string;
  lenderDetails: string;
  contractNote: string;
}

/**
 * The rate input's prefill for a stored ANNUAL bps + display period: a monthly
 * quote shows `annual/12` only when that division is exact (integer bps —
 * always true for a rate stored via {@link rateInputToAnnualBps}); anything
 * else falls back to the annual quote so the shown number × period is ALWAYS
 * exactly what would be saved back.
 */
export function ratePeriodPrefill(
  annualRateBps: number,
  ratePeriod: RatePeriod,
): { rateText: string; ratePeriod: RatePeriod } {
  if (ratePeriod === 'monthly' && annualRateBps % 12 === 0) {
    return { rateText: formatBpsForInput(annualRateBps / 12), ratePeriod: 'monthly' };
  }
  return { rateText: formatBpsForInput(annualRateBps), ratePeriod: 'annual' };
}

/** Editor initial state: prefills from an existing loan (+ its stored contract
 *  block for a contract-sourced loan), or a blank create form. */
export function loanToFormFields(loan: Loan | null, contract: LoanContract | null = null): LoanFormFields {
  if (!loan) {
    return {
      name: '',
      lender: '',
      scheduleType: 'annuity',
      principalText: '',
      rateText: '',
      ratePeriod: 'annual',
      termText: '',
      firstPaymentDate: '',
      residualText: '',
      hasDrawdown: false,
      drawdownDate: '',
      drawdownText: '',
      loanKind: 'bank',
      shareholder: '',
      interestMode: 'accrued',
      ownerNote: '',
      contractNo: '',
      feeDate: '',
      feeText: '',
      collateral: '',
      sureties: '',
      lenderDetails: '',
      contractNote: '',
    };
  }
  const rate = ratePeriodPrefill(loan.annualRateBps, loan.ratePeriod);
  return {
    name: loan.name,
    lender: loan.lender,
    scheduleType: loan.scheduleType,
    principalText: formatCentsForInput(loan.principalCents),
    rateText: rate.rateText,
    ratePeriod: rate.ratePeriod,
    termText: String(loan.termMonths),
    firstPaymentDate: loan.firstPaymentDate,
    residualText: loan.residualValueCents === null ? '' : formatCentsForInput(loan.residualValueCents),
    hasDrawdown: loan.drawdownDate !== null,
    drawdownDate: loan.drawdownDate ?? '',
    drawdownText: loan.drawdownCents === null ? '' : formatCentsForInput(loan.drawdownCents),
    loanKind: loan.loanKind,
    shareholder: loan.ownerTerms?.shareholder ?? '',
    interestMode: loan.ownerTerms?.interestMode ?? 'accrued',
    ownerNote: loan.ownerTerms?.note ?? '',
    contractNo: contract?.contractNo ?? '',
    feeDate: contract?.fee?.date ?? '',
    feeText: contract?.fee ? formatCentsForInput(contract.fee.cents) : '',
    collateral: contract?.collateral ?? '',
    sureties: contract?.sureties ?? '',
    lenderDetails: contract?.lenderDetails ?? '',
    contractNote: contract?.note ?? '',
  };
}

/** Import-review initial state: the server prefill → the SAME form shape the
 *  editor uses (one field/draft pipeline for both surfaces). */
export function prefillToFormFields(prefill: ContractImportPrefill): LoanFormFields {
  const blank = loanToFormFields(null);
  const rate =
    prefill.annualRateBps === null
      ? { rateText: '', ratePeriod: prefill.ratePeriod }
      : ratePeriodPrefill(prefill.annualRateBps, prefill.ratePeriod);
  return {
    ...blank,
    name: prefill.contractNo === null ? '' : `Laen ${prefill.contractNo}`,
    lender: prefill.lender ?? '',
    principalText: prefill.principalCents === null ? '' : formatCentsForInput(prefill.principalCents),
    rateText: rate.rateText,
    ratePeriod: rate.ratePeriod,
    termText: String(prefill.termMonths),
    firstPaymentDate: prefill.firstPaymentDate ?? '',
    contractNo: prefill.contractNo ?? '',
    feeDate: prefill.fee?.date ?? '',
    feeText: prefill.fee === null ? '' : formatCentsForInput(prefill.fee.cents),
    collateral: prefill.collateral ?? '',
    sureties: prefill.sureties ?? '',
    lenderDetails: prefill.lenderDetails ?? '',
  };
}

/** Trimmed optional contract text → the API's `min(1).nullish()` shape. */
function trimmedOrNull(text: string): string | null {
  const trimmed = text.trim();
  return trimmed ? trimmed : null;
}

/**
 * Assemble a valid write body from the raw form, or null while the form is
 * incomplete/invalid (the Save button follows this). Every rule here mirrors the
 * API's Zod body + finance-core's `assertLoanTerms`, so a non-null draft is
 * guaranteed to both preview and save. Passing `scheduleRows` switches the form
 * into CONTRACT mode: the draft additionally requires the contract facts, and is
 * null until the rows chain-validate against the very terms being saved (the
 * SAME finance-core check the API re-runs).
 */
export function buildLoanDraft(
  fields: LoanFormFields,
  scheduleRows: readonly ImportedScheduleRow[] | null = null,
): LoanDraft | null {
  const name = fields.name.trim();
  const lender = fields.lender.trim();
  const principalCents = parseEurInput(fields.principalText);
  const annualRateBps = rateInputToAnnualBps(fields.rateText, fields.ratePeriod);
  const termMonths = parseTermInput(fields.termText);
  if (!name || !lender || principalCents === null || annualRateBps === null || termMonths === null) {
    return null;
  }
  if (!isValidIsoDate(fields.firstPaymentDate)) {
    return null;
  }

  let contract: LoanContractDraft | undefined;
  if (scheduleRows !== null) {
    const contractNo = fields.contractNo.trim();
    if (!contractNo) {
      return null;
    }
    // The fee is ONE dated pair — both fields set (and valid) or both empty.
    let fee: ContractFee | null = null;
    const hasFeeInput = fields.feeDate.trim() !== '' || fields.feeText.trim() !== '';
    if (hasFeeInput) {
      const feeCents = parseEurInput(fields.feeText);
      if (!isValidIsoDate(fields.feeDate) || feeCents === null) {
        return null;
      }
      fee = { date: fields.feeDate, cents: feeCents };
    }
    try {
      // The finance-core chain check against the terms being saved — a draft
      // only materializes when the API's superRefine would accept it too.
      validateImportedSchedule(scheduleRows, {
        principalCents,
        termMonths,
        firstPaymentDate: fields.firstPaymentDate,
      });
    } catch {
      return null;
    }
    contract = {
      contractNo,
      fee,
      collateral: trimmedOrNull(fields.collateral),
      sureties: trimmedOrNull(fields.sureties),
      lenderDetails: trimmedOrNull(fields.lenderDetails),
      note: trimmedOrNull(fields.contractNote),
      scheduleRows: [...scheduleRows],
    };
  }

  let residualValueCents: number | undefined;
  if (fields.scheduleType === 'balloon') {
    const residual = parseEurInput(fields.residualText);
    if (residual === null || residual >= principalCents) {
      return null;
    }
    residualValueCents = residual;
  }

  let drawdown: { date: string; cents: number } | null = null;
  if (fields.hasDrawdown) {
    const cents = parseEurInput(fields.drawdownText);
    if (!isValidIsoDate(fields.drawdownDate) || cents === null) {
      return null;
    }
    drawdown = { date: fields.drawdownDate, cents };
  }

  const base = {
    name,
    lender,
    scheduleType: fields.scheduleType,
    principalCents,
    annualRateBps,
    ratePeriod: fields.ratePeriod,
    termMonths,
    firstPaymentDate: fields.firstPaymentDate,
    ...(residualValueCents !== undefined ? { residualValueCents } : {}),
    drawdown,
    ...(contract !== undefined ? { contract } : {}),
  };

  if (fields.loanKind === 'owner') {
    const shareholder = fields.shareholder.trim();
    if (!shareholder) {
      return null;
    }
    const note = fields.ownerNote.trim();
    return {
      loanKind: 'owner',
      ...base,
      ownerTerms: { shareholder, interestMode: fields.interestMode, note: note ? note : null },
    };
  }
  if (fields.loanKind === 'tax_debt') {
    // Ajatatud maksuvõlg (EMTA): the decision's amounts are FINAL — rate is 0
    // by definition, the verbatim decision schedule is required and there is
    // no drawdown (mirrors the API's tax_debt union arm 1:1).
    if (annualRateBps !== 0 || contract === undefined || drawdown !== null) {
      return null;
    }
    return { loanKind: 'tax_debt', ...base };
  }
  return { loanKind: 'bank', ...base };
}

/* ------------------------------------------------------------------ derivations */

export interface SchedulePreview {
  rows: LoanInstallment[];
  totals: LoanScheduleTotals;
}

/**
 * The editor's live schedule preview, through THE finance-core installment
 * provider (`loanInstallments`) — a contract draft previews its imported annex
 * rows, a computed draft its derived schedule; the two can never fork. A null
 * draft (incomplete form) previews nothing; a non-null draft is already
 * validated (buildLoanDraft enforces the same invariants, including the
 * imported chain), so this never throws.
 */
export function buildSchedulePreview(draft: LoanDraft | null): SchedulePreview | null {
  if (!draft) {
    return null;
  }
  const rows = loanInstallments(
    draft.contract
      ? { scheduleSource: 'contract', terms: toLoanTerms(draft), importedRows: draft.contract.scheduleRows }
      : { scheduleSource: 'computed', terms: toLoanTerms(draft) },
  );
  return { rows, totals: loanScheduleTotals(rows) };
}

function toLoanTerms(loan: {
  principalCents: number;
  annualRateBps: number;
  termMonths: number;
  firstPaymentDate: string;
  scheduleType: LoanScheduleType;
  residualValueCents?: number | null;
}): LoanTerms {
  return {
    principalCents: loan.principalCents,
    annualRateBps: loan.annualRateBps,
    termMonths: loan.termMonths,
    firstPaymentDate: loan.firstPaymentDate,
    scheduleType: loan.scheduleType,
    ...(loan.residualValueCents ? { residualValueCents: loan.residualValueCents } : {}),
  };
}

/** True when this loan books NO monthly interest rows (interest accrues to balance). */
export function suppressesInterest(loan: Loan): boolean {
  return loan.loanKind === 'owner' && loan.ownerTerms?.interestMode === 'accrued';
}

export type LoanPhase = 'pendingDrawdown' | 'active' | 'finished';

/** Today-anchored portfolio row facts, all derived from finance-core's schedule. */
export interface LoanTodayFacts {
  /** Principal still owed as of `today` (before today's own installment). */
  remainingCents: number;
  /** Next nominal due date (ISO) — null once the schedule is fully in the past. */
  nextDueDate: string | null;
  /** What the next installment actually books to the calendar (interest-suppressed for accruing owner loans). */
  nextPaymentCents: number | null;
  phase: LoanPhase;
}

/** The provider input for one ROSTER loan: a computed loan derives its schedule
 *  from the terms; a contract loan replays its stored annex rows. Null while a
 *  contract loan's rows are not loaded yet — the caller renders a placeholder,
 *  NEVER a forked (terms-derived) number for a contract loan. */
function toScheduleInput(
  loan: Loan,
  importedRows: readonly ImportedScheduleRow[] | null,
): { scheduleSource: LoanScheduleSource; terms: LoanTerms; importedRows?: readonly ImportedScheduleRow[] } | null {
  if (loan.scheduleSource === 'contract') {
    if (importedRows === null || importedRows.length === 0) {
      return null;
    }
    return { scheduleSource: 'contract', terms: toLoanTerms(loan), importedRows };
  }
  return { scheduleSource: 'computed', terms: toLoanTerms(loan) };
}

/**
 * Derive one loan's table row: remaining balance, next payment and phase as of
 * `today`. An installment due TODAY counts as upcoming (not yet paid). A
 * contract-sourced loan needs its stored annex rows (`importedRows`) — until
 * they are loaded this answers null and the roster shows a placeholder (one
 * truth per number: the annuity math is NOT that loan's schedule).
 */
export function loanTodayFacts(
  loan: Loan,
  today: string,
  importedRows: readonly ImportedScheduleRow[] | null = null,
): LoanTodayFacts | null {
  const input = toScheduleInput(loan, importedRows);
  if (input === null) {
    return null;
  }
  const rows = loanInstallments(input);
  const suppress = suppressesInterest(loan);
  let remainingCents = loan.principalCents;
  for (const row of rows) {
    if (row.dueDate >= today) {
      return {
        remainingCents,
        nextDueDate: row.dueDate,
        nextPaymentCents: suppress ? row.principalCents : row.totalCents,
        phase: loan.drawdownDate !== null && loan.drawdownDate >= today ? 'pendingDrawdown' : 'active',
      };
    }
    remainingCents = row.remainingCents;
  }
  return { remainingCents: 0, nextDueDate: null, nextPaymentCents: null, phase: 'finished' };
}

export interface PortfolioSummary {
  /** Σ remaining principal across the portfolio. */
  remainingCents: number;
  /** Σ next calendar-booked installments of unfinished loans (monthly service). */
  monthlyServiceCents: number;
  /** Remaining-balance-weighted average rate in bps — null with no outstanding balance. */
  weightedRateBps: number | null;
}

/**
 * The three summary cards, from the same per-loan facts as the table rows.
 * `contractRowsByLoan` carries each contract-sourced loan's stored annex rows;
 * while ANY of them is still missing the summary answers null (the cards show a
 * placeholder) — a partial sum would be a silently wrong number.
 */
export function portfolioSummary(
  loans: Loan[],
  today: string,
  contractRowsByLoan: ReadonlyMap<string, readonly ImportedScheduleRow[]> = new Map(),
): PortfolioSummary | null {
  let remainingCents = 0;
  let monthlyServiceCents = 0;
  let weighted = 0;
  let rateBaseCents = 0;
  for (const loan of loans) {
    const facts = loanTodayFacts(loan, today, contractRowsByLoan.get(loan.id) ?? null);
    if (facts === null) {
      return null;
    }
    remainingCents += facts.remainingCents;
    monthlyServiceCents += facts.nextPaymentCents ?? 0;
    // A deferred tax debt carries NO rate (its amounts are final), so it counts
    // in the balance + monthly service but is EXCLUDED from the weighted-rate
    // average — a 0 %-weighted giant would silently drag the real loan rate down.
    if (loan.loanKind !== 'tax_debt') {
      weighted += facts.remainingCents * loan.annualRateBps;
      rateBaseCents += facts.remainingCents;
    }
  }
  return {
    remainingCents,
    monthlyServiceCents,
    weightedRateBps: rateBaseCents > 0 ? Math.round(weighted / rateBaseCents) : null,
  };
}

/* ------------------------------------------------- tax-debt paste parsing */

const ET_DATE = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/;

/** Estonian "28.02.2026" (or an ISO day) → "2026-02-28"; null when not a real
 *  calendar day. Pure format translation — no date arithmetic happens here. */
export function parseEtDateInput(text: string): string | null {
  const trimmed = text.trim();
  if (isValidIsoDate(trimmed)) {
    return trimmed;
  }
  const match = ET_DATE.exec(trimmed);
  if (!match) {
    return null;
  }
  const iso = `${match[3]}-${match[2].padStart(2, '0')}-${match[1].padStart(2, '0')}`;
  return isValidIsoDate(iso) ? iso : null;
}

export interface TaxSchedulePasteResult {
  /** Parsed (date, amount) pairs in paste order. */
  items: TaxScheduleItem[];
  /** 1-based numbers of non-empty lines that did not parse. */
  invalidLines: number[];
}

/**
 * Parse a pasted EMTA decision schedule — one row per line, `dd.MM.yyyy amount`
 * ("28.02.2026 4 500,00"). Dates and amounts reuse the SAME parsers as every
 * other money/date input; nothing is computed here — the verbatim chain itself
 * comes from finance-core's {@link buildTaxScheduleRows}.
 */
export function parseTaxScheduleText(text: string): TaxSchedulePasteResult {
  const items: TaxScheduleItem[] = [];
  const invalidLines: number[] = [];
  text.split('\n').forEach((line, index) => {
    const trimmed = line.trim();
    if (!trimmed) {
      return;
    }
    const match = /^(\S+)\s+(.+)$/.exec(trimmed);
    const dueDate = match ? parseEtDateInput(match[1]) : null;
    const cents = match ? parseEurInput(match[2]) : null;
    if (dueDate === null || cents === null) {
      invalidLines.push(index + 1);
      return;
    }
    items.push({ dueDate, cents });
  });
  return { items, invalidLines };
}

/** Pasted items → the verbatim annex rows, or the builder's own refusal
 *  (duplicate day, too many rows …) surfaced verbatim — never swallowed. */
export function taxScheduleRowsFromItems(
  items: readonly TaxScheduleItem[],
): { rows: ImportedScheduleRow[]; error: null } | { rows: null; error: string } {
  try {
    return { rows: buildTaxScheduleRows(items), error: null };
  } catch (err) {
    // The builder only ever refuses via RangeError (finance-core contract).
    return { rows: null, error: (err as RangeError).message };
  }
}

/** How many annex rows are already past due on import day (dueDate < today) —
 *  the review voices that these will materialize as overdue calendar rows. */
export function countPastDueRows(rows: readonly ImportedScheduleRow[], today: string): number {
  return rows.filter((row) => row.dueDate < today).length;
}

/* ------------------------------------------------------------------ payroll */

export const MONTH_LABELS_ET = [
  'Jaanuar',
  'Veebruar',
  'Märts',
  'Aprill',
  'Mai',
  'Juuni',
  'Juuli',
  'August',
  'September',
  'Oktoober',
  'November',
  'Detsember',
] as const;

export interface PayrollYearRow {
  /** `YYYY-MM`. */
  month: string;
  label: (typeof MONTH_LABELS_ET)[number];
  data: PayrollMonth | null;
}

/** The 12-row grid for one year, joined against whatever months are saved. */
export function buildPayrollYear(year: number, months: PayrollMonth[]): PayrollYearRow[] {
  const byMonth = new Map(months.map((m) => [m.month, m]));
  return MONTH_LABELS_ET.map((label, index) => {
    const month = `${year}-${String(index + 1).padStart(2, '0')}`;
    return { month, label, data: byMonth.get(month) ?? null };
  });
}

export interface PayrollAverages {
  netFundCents: number;
  tsdCents: number;
}

/** Display-only mean of the SAVED months (integer cents; null with none saved). */
export function payrollAverages(months: PayrollMonth[]): PayrollAverages | null {
  if (months.length === 0) {
    return null;
  }
  let net = 0;
  let tsd = 0;
  for (const m of months) {
    net += m.netFundCents;
    tsd += m.tsdCents;
  }
  return { netFundCents: Math.round(net / months.length), tsdCents: Math.round(tsd / months.length) };
}

/* ------------------------------------------------------------------ formatting */

const RATE_FORMAT = new Intl.NumberFormat('et-EE', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
const INPUT_EUR_FORMAT = new Intl.NumberFormat('et-EE', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
  useGrouping: false,
});

/** Basis points → Estonian percent text ("490" → "4,9%"). */
export function formatRateBps(bps: number): string {
  return `${RATE_FORMAT.format(bps / 100)}%`;
}

/** Cents → a plain editable Estonian amount ("25000000" → "250000,00"). */
export function formatCentsForInput(cents: number): string {
  return INPUT_EUR_FORMAT.format(cents / 100);
}

/** Basis points → a plain editable percent ("490" → "4,9"). */
export function formatBpsForInput(bps: number): string {
  return RATE_FORMAT.format(bps / 100);
}

/** The device clock as an ISO day — the view's default `today` (tests inject). */
export function currentIsoDate(): string {
  return new Date().toISOString().slice(0, 10);
}
